import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Lock, LogIn, Eye, EyeOff, Crown, Check, Heart, TrendingDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

interface QuickAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onUpgradeToPremium?: () => void;
  title?: string;
}

const QuickAuthModal: React.FC<QuickAuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onUpgradeToPremium,
  title = "Get access to premium discount links"
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isNewUser, setIsNewUser] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [error, setError] = useState('');
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, register } = useAuth();

  // ✅ Debounce email input - only trigger API call 800ms after user stops typing
  const debouncedEmail = useDebounce(email, 800);

  // ✅ Validate email format (must be complete email with domain)
  const isValidEmail = useCallback((emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  }, []);

  // Save and restore scroll position
  useEffect(() => {
    if (isOpen) {
      setSavedScrollPosition(window.scrollY);
      // Reset form state when modal opens
      setEmail('');
      setPassword('');
      setIsNewUser(null);
      setError('');
      setResetEmailSent(false);
      setRegistrationSuccess(false);
    } else {
      if (savedScrollPosition > 0) {
        window.scrollTo(0, savedScrollPosition);
      }
    }
  }, [isOpen, savedScrollPosition]);

  // ✅ Check email exists only when debouncedEmail changes AND format is valid
  useEffect(() => {
    // Skip if modal is not open or email is empty
    if (!isOpen || !debouncedEmail) {
      return;
    }

    // Only check if email format is complete (e.g., user@domain.com)
    if (!isValidEmail(debouncedEmail)) {
      setIsNewUser(null);
      return;
    }

    // Perform the API check
    const checkEmailExists = async () => {
      setCheckingEmail(true);
      setError('');

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/auth/check-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: debouncedEmail })
        });

        const data = await response.json();
        
        if (data.success) {
          setIsNewUser(!data.exists);
        } else {
          // Don't show error for email check - it might just be a transient issue
          console.warn('Email check returned unsuccessful:', data);
        }
      } catch (error) {
        console.error('Email check error:', error);
        // Don't show error to user during typing - let them continue
      } finally {
        setCheckingEmail(false);
      }
    };

    checkEmailExists();
  }, [debouncedEmail, isOpen, isValidEmail]);

  if (!isOpen) return null;

  // ✅ Simplified email change handler - just update state, debounce handles the rest
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError('');
    // Reset user status when email changes
    if (isNewUser !== null) {
      setIsNewUser(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (isNewUser === null) {
      setError('Please wait for email verification');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = isNewUser 
        ? await register(email, password)
        : await login(email, password);

      if (result.success) {
        if (isNewUser) {
          // For new users, show success message instead of immediately closing
          setError('');
          setRegistrationSuccess(true);
          // Give user time to see success message
          setTimeout(() => {
            onSuccess();
          }, 2000);
        } else {
          // For existing users, close immediately
          onSuccess();
        }
      } else {
        setError(result.message || 'Authentication failed');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (data.success) {
        setResetEmailSent(true);
      } else {
        setError(data.message || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getActionText = () => {
    if (checkingEmail) return 'Checking...';
    if (isNewUser === true) return 'Create Account & Continue';
    if (isNewUser === false) return 'Sign In & Continue';
    return 'Continue';
  };

  const getStatusText = () => {
    if (checkingEmail) return 'Verifying email...';
    if (isNewUser === true) return '✨ New email - we\'ll create your account';
    if (isNewUser === false) return '👋 Welcome back!';
    return '';
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
    >
      <div
        className="bg-white max-w-md w-full rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-orange-100">
                <LogIn className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                Sign In / Create Account
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-6 text-center">
            {title}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="quick-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="quick-email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter your email"
                  required
                />
                {checkingEmail && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                  </div>
                )}
              </div>
              {getStatusText() && (
                <p className="text-xs text-gray-500 mt-1">{getStatusText()}</p>
              )}
            </div>

            <div>
              <label htmlFor="quick-password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="quick-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder={isNewUser ? "Create a password" : "Enter your password"}
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {isNewUser && (
                <p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters</p>
              )}
              {isNewUser === false && !resetEmailSent && (
                <div className="mt-1 text-right">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    className="text-xs text-orange-600 hover:text-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            {registrationSuccess && (
              <div className="bg-green-50 text-green-800 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="bg-green-500 rounded-full p-1">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <p className="font-bold text-lg">Welcome aboard!</p>
                </div>
                
                <p className="text-sm mb-3">You're now signed in and can:</p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-sm">
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span>Browse all shoe recommendations</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Heart className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span>Save your favorite shoes</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <TrendingDown className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span>View price history charts</span>
                  </div>
                </div>

                {onUpgradeToPremium && (
                  <div className="border-t border-green-200 pt-3 mt-3">
                    <div className="flex items-center space-x-2 text-sm text-green-700 mb-2">
                      <Crown className="h-4 w-4 text-amber-500" />
                      <span>Upgrade to Premium to unlock purchase links</span>
                    </div>
                    <p className="text-xs text-green-600">Save $50-$100 on every purchase!</p>
                  </div>
                )}
              </div>
            )}

            {resetEmailSent && (
              <div className="bg-green-50 text-green-800 p-3 rounded-lg text-sm">
                <p className="font-medium">Password reset email sent!</p>
                <p className="mt-1">Check your email for a link to reset your password. It may take a few minutes to arrive.</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-800 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {!resetEmailSent && !registrationSuccess ? (
              <button
                type="submit"
                disabled={loading || checkingEmail || isNewUser === null}
                className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{isNewUser ? 'Creating Account...' : 'Signing In...'}</span>
                  </div>
                ) : (
                  <span>{getActionText()}</span>
                )}
              </button>
            ) : registrationSuccess ? (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={onSuccess}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Continue Browsing
                </button>
                {onUpgradeToPremium && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onUpgradeToPremium();
                    }}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <Crown className="h-4 w-4" />
                    <span>Upgrade to Premium</span>
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Close
              </button>
            )}
          </form>

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAuthModal;